import type { CheckStatus } from './types.js';

export function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function formatTimeOfDay(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Format a YYYY-MM-DD date string (UTC-aligned, as stored by the
 * uptime aggregator) as a human-readable local date. The "T12:00:00Z"
 * anchor avoids tipping into the previous/next day for users east or
 * west of UTC.
 */
export function formatLocalDate(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.round(hr / 24);
  return `${day} d ago`;
}

/** Human label for a check status. */
export function statusWord(s: CheckStatus): string {
  switch (s) {
    case 'up': return 'Operational';
    case 'degraded': return 'Degraded';
    case 'down': return 'Outage';
  }
}

/**
 * Map a check status to an MUI palette token. Returned strings are theme
 * colour paths the consumer can resolve via `theme.palette.<path>` or
 * MUI sx (`color: 'success.main'`).
 */
export function statusPaletteToken(s: CheckStatus): string {
  switch (s) {
    case 'up': return 'success.main';
    case 'degraded': return 'warning.main';
    case 'down': return 'error.main';
  }
}

/** Pick MUI Alert severity for a list of incidents. */
export function bannerSeverity(
  incidents: Array<{ severity: string; current_status: string }>,
): 'error' | 'warning' | 'info' {
  if (incidents.some((i) => i.severity === 'critical')) return 'error';
  if (incidents.some((i) => i.current_status !== 'resolved')) return 'warning';
  return 'info';
}
